import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useProfile } from '@/hooks/use-profile';

const NeedInvite = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { profile } = useProfile(user?.id);
  
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);

  // Check if user already has a profile
  useEffect(() => {
    if (profile) {
      console.log("User already has a profile, redirecting to bulletins");
      navigate('/bulletins');
    }
  }, [profile, navigate]);

  const handleRetry = async () => {
    setIsConnecting(true);
    try {
      // Check for invite code in URL
      const urlParams = new URLSearchParams(window.location.search);
      const inviteCode = urlParams.get('code');

      if (!inviteCode) {
        console.log('No invite code found');
        toast({
          title: "Invite Required",
          description: "You need a valid invite link to join.",
          variant: "destructive"
        });
        return;
      }

      // Verify invite code is valid
      const { data: invite, error: inviteError } = await supabase
        .from('invites')
        .select('*')
        .eq('code', inviteCode)
        .maybeSingle();

      if (inviteError || !invite || (invite.is_redeemed && !invite.is_timed)) {
        console.log('Invalid or already used invite code');
        toast({
          title: "Invalid Invite",
          description: "This invite code is invalid or has already been used.",
          variant: "destructive"
        });
        return;
      }

      // Attempt to login with Supabase
      await login();

    } catch (error: any) {
      console.error('Connection error:', error);
      toast({
        title: "Connection Error",
        description: error.message || "Failed to connect. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A1F2C] p-4">
      <div className="w-full max-w-md space-y-8 text-center">
        <h1 className="text-3xl font-bold text-white">Welcome to FOMO</h1>
        <div className="space-y-4 text-gray-400">
          <p>
            To join FOMO, you need an invite link from an existing member.
          </p>
          <p>
            Ask a friend for their invite link and click it to get started.
          </p>
          <p className="text-sm">
            The link should look like: fomo.app/invite?code=xyz123
          </p>
        </div>
        <div className="space-y-4">
          <Button
            onClick={handleRetry}
            className="w-full"
            disabled={isConnecting}
          >
            {isConnecting ? "Connecting..." : "Try Again"}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="w-full"
          >
            Return to Landing Page
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NeedInvite;
